import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Select,
    Stack,
    Text
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { personsService } from "services/personsService";
import { permissionsService } from "services/permissionsService";
import { profilesService } from "services/profilesService";
import { userService } from "services/userService";
import { UserRole } from "services/userService";
import { useCustomToast } from "components/CustomToast/CustomToast";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Loading from "components/Loading/Loading";
import SearchSelect from "components/SearchSelect/SearchSelect";
import SwitchInput from "components/SwitchInput/SwitchInput";
import { ROUTES } from "constants/routes-path";
import ProfileSearchSelect from "./ProfileSearchSelect";
import { checkErrors, hasError } from "utils/forms-utils";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { departmentService } from "../../../services/departmentService";

function UserNew({ data }) {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const showToast = useCustomToast();

    const [userPersons, setUserPersons] = useState(undefined);
    const [userProfiles, setUserProfiles] = useState(undefined);
    const [userPermissions, setUserPermissions] = useState(undefined);
    const [departments, setDepartments] = useState(undefined);
    const [formData, setFormData] = useState(undefined);
    const [inputError, setInputError] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    const userRoles = useMemo(() => Object.values(UserRole), []);
    const ids = useMemo(() => ({
        userId: "id",
        userPerson: "person",
        userLogin: "login",
        userPassword: "password",
        userRegistration: "registration",
        userRole: "role",
        userIsActive: "active",
        userProfiles: "profiles",
        userPermissions: "permissions"
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

    const initFormData = (data) => ({
        [ids.userId]: {
            value: data?.id || "",
            validations: [],
        },
        [ids.userPerson]: {
            value: data?.pessoa?.id || "",
            validations: [{ required: true }],
        },
        [ids.userLogin]: {
            value: data?.login || "",
            validations: [{ required: true }],
        },
        [ids.userPassword]: {
            value: "",
            validations: id ? [] : [{ required: true }],
        },
        [ids.userRegistration]: {
            value: data?.matricula || null,
            validations: data?.matricula ? [{ required: true }] : [],
        },
        [ids.userRole]: {
            value: Object.values(UserRole).some(r => r.value === data?.funcao) ? data?.funcao : "",
            validations: id ? [] : [{ required: true }],
        },
        [ids.userIsActive]: {
            value: data?.ativo ?? true,
            validations: [],
        },
        [ids.userProfiles]: {
            value: data?.perfis || [],
            validations: [],
        },
        [ids.userPermissions]: data?.permissoes?.map(item => ({
            permissionId: item.permissao?.id,
            adminUnitId: item.unidade?.id
        })) || [],
        userAdminUnit: { value: data?.userAdminUnit?.value || "" }
    });

    const fetchData = async () => {
        try {
            const userPersons = await personsService.findMany();
            const userPermissions = await permissionsService.list();
            const userProfiles = await profilesService.list();
            const departments = await departmentService.list();
            setUserPersons(userPersons?._embedded?.person_list);
            setUserPermissions(userPermissions?._embedded?.permission_list);
            setDepartments(departments?._embedded?.content);
            setUserProfiles(userProfiles?._embedded?.profile_list);

            if (id) {
                const userDetails = await userService.find(+id);
                setFormData(initFormData(userDetails));
                setInputError(checkErrors({ formData: initFormData(userDetails), inputError: {} }));
            } else {
                setFormData(initFormData());
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
            navigate(ROUTES.management + ROUTES.users);
        }
    }

    useEffect(() => {
        fetchData();
    }, [id]);

    const redirectToList = () => {
        navigate(ROUTES.usersPath);
    }

    const handleSave = async () => {
        if (isFormInvalid) return;

        try {
            const data = toDTO();
            if (id) {
                await userService.update(id, data);
                showToast({ title: t("settings.user.messages.success.edit"), status: "success" });
            } else {
                await userService.create(data);
                showToast({ title: t("settings.user.messages.success.create"), status: "success" });
            }
            redirectToList();
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }

    };

    const handleInputChange = (e) => {
        const { id, type } = e.target;
        const value = type === "checkbox" ? e.target.checked : e.target.value;

        const newFormData = { ...formData, [id]: { ...formData[id], value: value } };
        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };

    const toDTO = () => ({
        id_pessoa: formData[ids.userPerson].value,
        login: formData[ids.userLogin].value,
        ...formData[ids.userPassword].value ? { senha: formData[ids.userPassword].value } : {},
        matricula: formData[ids.userRegistration].value,
        funcao: formData[ids.userRole].value,
        ativo: formData[ids.userIsActive].value,
        id_perfil: formData[ids.userProfiles].value ? formData[ids.userProfiles].value.map(profile => profile.id) : [],
        horarios_acesso: [],
        permissoes: formData[ids.userPermissions] || [],
        id: formData[ids.userId].value,
    });

    return (
        <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
            <Card>
                <ListPageCardHeader
                    title={t(`settings.user.${id ? "edit" : "new"}.title`)}
                    subtitle={t(`settings.user.${id ? "edit" : "new"}.subtitle`)}
                />
                <CardBody px="22px">
                    {formData && userPersons && userRoles ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={inputError[ids.userPerson]}>
                                    <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                                        {t('settings.user.new.label.person')}
                                    </FormLabel>
                                    <SearchSelect
                                        required
                                        id={ids.userPerson}
                                        value={formData[ids.userPerson].value}
                                        isDisabled={!userPersons.length}
                                        items={userPersons}
                                        valueField="id"
                                        displayField="nome"
                                        onChange={handleInputChange}
                                        placeholder={t('settings.user.new.placeholder.person')}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.userPerson])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={inputError[ids.userLogin]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t('settings.user.new.label.login')}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        variant="main"
                                        id={ids.userLogin}
                                        value={formData[ids.userLogin].value}
                                        onChange={handleInputChange}
                                        placeholder={t('settings.user.new.placeholder.login')}
                                        maxLength={50}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.userLogin])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isRequired isInvalid={inputError[ids.userPassword]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t('settings.user.new.label.password')}
                                    </FormLabel>
                                    <InputGroup>
                                        <Input
                                            fontSize="sm"
                                            variant="main"
                                            id={ids.userPassword}
                                            value={formData[ids.userPassword].value}
                                            onChange={handleInputChange}
                                            type={showPassword ? "text" : "password"}
                                            placeholder={t('settings.user.new.placeholder.password')}
                                            maxLength={255}
                                        />
                                        <InputRightElement>
                                            <IconButton
                                                aria-label="Show password"
                                                icon={showPassword ? <HiEyeOff /> : <HiEye />}
                                                onClick={togglePasswordVisibility}
                                                variant="link"
                                                size="sm"
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                    <FormErrorMessage>
                                        {t(inputError[ids.userPassword])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired={!!id}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t('settings.user.new.label.registration')}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        variant="main"
                                        id={ids.userRegistration}
                                        value={formData[ids.userRegistration].value}
                                        onChange={handleInputChange}
                                        placeholder={t('settings.user.new.placeholder.registration')}
                                        maxLength={255}
                                    />
                                </FormControl>

                                <FormControl isRequired={!id} isInvalid={inputError[ids.userRole]}>
                                    <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                                        {t('settings.user.new.label.role')}
                                    </FormLabel>
                                    <SearchSelect
                                        required={!id}
                                        id={ids.userRole}
                                        value={formData[ids.userRole].value}
                                        isDisabled={!userRoles.length}
                                        items={userRoles}
                                        valueField="value"
                                        displayField="value"
                                        onChange={handleInputChange}
                                        placeholder={t('settings.user.new.placeholder.role')}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.userRole])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.userIsActive]}>
                                    <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                                        {t('settings.user.new.label.is-active')}
                                    </FormLabel>
                                    <SwitchInput
                                        name={ids.userIsActive}
                                        defaultChecked={formData[ids.userIsActive].value}
                                        onChange={(e) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                [e.name]: {
                                                    ...prev[e.name],
                                                    value: e.value,
                                                },
                                            }));
                                        }}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.userIsActive])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.userProfiles]}>
                                    <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                                        {t('settings.user.new.label.profiles')}
                                    </FormLabel>
                                    <ProfileSearchSelect
                                        required
                                        id={ids.userProfiles}
                                        value={formData[ids.userProfiles].value}
                                        isDisabled={!userProfiles.length}
                                        items={userProfiles}
                                        valueField="id"
                                        displayField="descricao"
                                        onChange={handleInputChange}
                                        placeholder={t('settings.user.new.placeholder.profiles')}
                                        defaultValue={formData[ids.userProfiles].value}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.userProfiles])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.userPermissions]}>
                                    <Stack>
                                        <HStack spacing={2} align="center">
                                            <FormLabel fontSize="xs" fontWeight="bold">
                                                {t('settings.user.new.label.permissions')}
                                            </FormLabel>
                                            <Button
                                                colorScheme="blue"
                                                size="sm"
                                                onClick={() => {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        [ids.userPermissions]: [
                                                            ...(prev[ids.userPermissions] || []),
                                                            { permissionId: "", adminUnitId: "", requiresAdminUnit: false }
                                                        ]
                                                    }));
                                                }}
                                            >
                                                + {t('settings.user.new.label.permission-new')}
                                            </Button>
                                        </HStack>

                                        {(formData[ids.userPermissions] || []).map((perm, index) => (
                                            <Stack key={index} spacing="10px" direction={formDirection} alignItems="flex-end">
                                                <FormControl isRequired>
                                                    <FormLabel fontSize="xs" fontWeight="bold">
                                                        {t('settings.user.new.label.permission')}
                                                    </FormLabel>
                                                    <Select
                                                        required
                                                        placeholder={t('settings.user.new.placeholder.permissions')}
                                                        fontSize="xs"
                                                        value={perm.permissionId || ""}
                                                        onChange={(e) => {
                                                            const selectedId = Number(e.target.value);
                                                            setFormData((prev) => {
                                                                const updatedPermissions = [...(prev[ids.userPermissions] || [])];
                                                                updatedPermissions[index] = {
                                                                    ...updatedPermissions[index],
                                                                    permissionId: selectedId,
                                                                    adminUnitId: ""
                                                                };
                                                                return { ...prev, [ids.userPermissions]: updatedPermissions };
                                                            });
                                                        }}
                                                    >
                                                        {userPermissions.map((permission) => (

                                                            <option key={permission.id} value={permission.id}>
                                                                {permission.sigla}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                <FormControl>
                                                    <FormLabel fontSize="xs" fontWeight="bold">
                                                        {t('settings.user.new.label.permission-admin-unit')}
                                                    </FormLabel>
                                                    <Select
                                                        placeholder={t('settings.user.new.placeholder.admin-unit')}
                                                        fontSize="xs"
                                                        value={perm.adminUnitId || ""}
                                                        onChange={(e) => {
                                                            setFormData((prev) => {
                                                                const updatedPermissions = [...(prev[ids.userPermissions] || [])];
                                                                updatedPermissions[index] = {
                                                                    ...updatedPermissions[index],
                                                                    adminUnitId: parseInt(e.target.value)
                                                                };
                                                                return { ...prev, [ids.userPermissions]: updatedPermissions };
                                                            });
                                                        }}
                                                    >
                                                        {Object.values(departments).map((unit) => (
                                                            <option key={unit.descricao} value={unit.id}>
                                                                {t(unit.descricao)}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                <Button
                                                    colorScheme="red"
                                                    mb="5px"
                                                    size="sm"
                                                    onClick={() => {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            [ids.userPermissions]: prev[ids.userPermissions].filter((_, i) => i !== index)
                                                        }));
                                                    }}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <FormErrorMessage>
                                        {t(inputError[ids.userPermissions])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack w="100%" direction="row" justify="space-between">
                                <Button
                                    w="125px"
                                    variant="no-effects"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={redirectToList}
                                >
                                    <Text>
                                        {t("system.actions.cancel")}
                                    </Text>
                                </Button>

                                <Button
                                    w="125px"
                                    variant="dark"
                                    fontSize="sm"
                                    isDisabled={isFormInvalid}
                                    onClick={handleSave}
                                    _hover={isFormInvalid ? {} : undefined}
                                >
                                    <Text>
                                        {t("system.actions.save")}
                                    </Text>
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
        </Flex>
    );

};

export default UserNew;